import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Listbox as HeadlessListBox, ListboxOptions, ListboxOption, ListboxButton } from '@headlessui/react';
import Chevron from '../../assets/icons/chevron-down.svg?react';
import styles from './Listbox.module.scss';
import { useFloating, flip, autoUpdate, size, shift, FloatingPortal } from '@floating-ui/react';
import { classnames } from '../../utils';

type ListboxProps<T> = {
  items: T[];
  width?: number;
  onSelectItem: (item: T) => void;
  defaultSelected?: T;
  disabled?: boolean;
};

export default function Listbox<T extends ListboxItem>({
  items = [],
  width: boxWidth,
  onSelectItem,
  defaultSelected,
  disabled
}: ListboxProps<T>) {
  const [selectedItem, setSelectedItem] = useState<T>(defaultSelected || items[0]);
  const [width, setWidth] = useState<number>(200);
  const [maxHeight, setMaxHeight] = useState<number>(150);
  const { refs, floatingStyles } = useFloating({
    middleware: [
      flip({
        fallbackAxisSideDirection: 'none',
        crossAxis: false
      }),
      size({
        apply({ availableHeight }) {
          flushSync(() => setMaxHeight(availableHeight - 10));
        }
      }),
      shift()
    ],
    whileElementsMounted: autoUpdate
  });

  useEffect(() => {
    if (
      (refs.reference.current as HTMLDivElement)?.offsetWidth &&
      (refs.reference.current as HTMLDivElement)?.offsetWidth >= width
    ) {
      setWidth((refs.reference.current as HTMLDivElement)?.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (items.length && !selectedItem && !defaultSelected) {
      setSelectedItem(items[0]);
    }
  }, [items, selectedItem, defaultSelected]);

  useEffect(() => {
    if (!defaultSelected) {
      setSelectedItem(items[0]);
    }
  }, [items.length, defaultSelected]);

  useEffect(() => {
    if (defaultSelected) {
      setSelectedItem(defaultSelected);
    }
  }, [defaultSelected?.id]);

  const selectItem = (item: T) => {
    setSelectedItem(item);
    onSelectItem(item);
  };

  return (
    <HeadlessListBox value={selectedItem} onChange={selectItem} disabled={disabled}>
      {({ open }) => (
        <div className={`${styles.wrapper} ${disabled ? styles.disabled : ''}`} style={{ maxWidth: boxWidth }}>
          <ListboxButton className={styles.button} ref={refs.setReference}>
            <span className={styles.buttonText}>
              <span>{selectedItem?.label}</span>
            </span>
            <span className={styles.buttonIcon}>
              <Chevron />
            </span>
          </ListboxButton>
          {open ? (
            <FloatingPortal>
              <ListboxOptions
                static
                modal={false}
                className={styles.options}
                ref={refs.setFloating}
                style={{
                  minWidth: `${width || 200}px`,
                  maxHeight,
                  ...floatingStyles
                }}
              >
                {items.map((item) => (
                  <ListboxOption
                    key={item.id}
                    value={item}
                    disabled={item.disabled}
                    className={({ focus, selected }) =>
                      classnames([
                        styles.option,
                        focus && styles.optionActive,
                        selected && styles.optionSelected,
                        item.isCategory && styles.optionCategory,
                        item.disabled && styles.optionDisabled,
                        item.isChild && styles.optionChild,
                        item.recommended && styles.recommended
                      ])
                    }
                  >
                    <span>{item.label}</span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </FloatingPortal>
          ) : null}
        </div>
      )}
    </HeadlessListBox>
  );
}
